import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { injectIntl } from 'react-intl';
// Import Shared Components
import {
  ActionBar,
  ButtonGroup,
  Input,
  Loader,
  SubmitButton,
  Title,
} from 'shared-components';
import { post } from 'shared-components/src/utils/http';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Component Styles
import {
  Form,
  FormGroup,
  InputContainer,
  StyledModal,
  StyledAlert,
  StyledButton,
  FieldError,
} from '../styles';
// Import Translations
import { recoverEn } from '../../../i18n/recover';
import InputGroup from '../../../components/InputGroup';

export default injectIntl(
  ({
    intl,
    virtualCardRecoveryDetailId,
    accountId,
    error,
    setError,
  }) => {
    const navigate = useNavigate();
    const initialFormState = {
      id: virtualCardRecoveryDetailId,
      firstName: '',
      lastName: '',
      email: '',
      accessCode: '',
    };

    const initialValidationErrors = {
      firstName: '',
      lastName: '',
      email: '',
      accessCode: '',
    };
    const [inputInfo, updateInputInfo] = useUpdateStateObject(initialFormState);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useUpdateStateObject(
      initialValidationErrors
    );
    const [agreementData, setAgreementData] = useState(null);
    const [isAgreementLoaded, setIsAgreementLoaded] = useState(false);
    const [isAgreementError, setIsAgreementError] = useState(false);

    const handleOnChange = (key, value) => {
      if (error !== null) setError(null);
      return updateInputInfo({ key, value });
    };

    const fetchAgreementLink = async () => {
      try {
        const res = await post('/api/v1/cardholder_portal/cardholder_agreement', {
          account_id: accountId,
        });

        if (res && res.error) {
          setIsAgreementError(true);
        } else if (res && res.data && res.data) {
          setAgreementData(res.data);
        } else {
          setIsAgreementError(true);
        }
      } catch (e) {
        setIsAgreementError(true);
      } finally {
        setIsAgreementLoaded(true);
      }
    };

    useEffect(() => {
      if (accountId && !isAgreementLoaded) {
        fetchAgreementLink();
      }
    }, [accountId, isAgreementLoaded]);
    const checkFormValidation = () => {
      const errors = validationErrors;

      // check firstName
      if (!inputInfo.firstName) {
        errors.firstName =
          inputInfo.firstName === ''
            ? 'First Name is required'
            : 'Invalid First Name. Please check and try again.';
      }

      // check lastName
      if (!inputInfo.lastName) {
        errors.lastName =
          inputInfo.lastName === ''
            ? 'Last Name is required'
            : 'Invalid Last Name. Please check and try again.';
      }

      // validate email for empty and with regex
      if (
        !inputInfo.email ||
        !inputInfo.email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
      ) {
        errors.email =
          inputInfo.email === ''
            ? 'Email is required'
            : 'Invalid Email Address. Please check and try again.';
      }

      // check accessCode
      if (!inputInfo.accessCode) {
        errors.accessCode =
          inputInfo.accessCode === ''
            ? 'Access Code is required'
            : 'Invalid Access Code. Please check and try again.';
      }

      setValidationErrors(errors);

      // Throws a generic error message if any of the fields are not valid
      const isInValid = Object.keys(validationErrors).find(
        field => validationErrors[field]
      );

      return isInValid;
    };

    const handleApiError = error => {
      let fieldName = '';

      switch (error.code) {
      case 'invalid_email':
        fieldName = 'email';
        break;
      case 'invalid_first_name':
        fieldName = 'firstName';
        break;
      case 'invalid_last_name':
        fieldName = 'lastName';
        break;
      case 'invalid_access_code':
        fieldName = 'accessCode';
        break;
      default:
        break;
      }
      setShowModal(false);
      if (fieldName) {
        setValidationErrors({
          key: fieldName,
          value: error.message || recoverEn[`recover-form-${fieldName}-invalid-error`],
        });
      } else {
        setError(
          error.message ||
            recoverEn['recover-form-error-alert']
        );
      }
    };
    const handleOnSubmit = async e => {
      e.preventDefault();

      const recoveryPayload = {
        id: virtualCardRecoveryDetailId,
        first_name: inputInfo.firstName,
        last_name: inputInfo.lastName,
        email: inputInfo.email,
        access_code: inputInfo.accessCode,
      };

      try {
        setLoading(true);
        const res = await fetch('/api/v1/virtual_card/recover/details', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(recoveryPayload),
        });

        const data = await res.json();

        // If an error occurred when recovering the card, we can display an error message
        if (data && data.error) {
          setLoading(false);
          throw data.error;
        }

        // Details have been verified and Card details have been recovered
        setLoading(false);

        const virtualCardDetails = { ...data.data, cardholder_fee_disclosure_url: agreementData?.cardholder_fee_disclosure_url || '' };
        // Iframe-enabled programs return a hosted iframe URL; keep card fields out of navigation state
        if (virtualCardDetails.iframe_url) {
          delete virtualCardDetails.card_number;
          delete virtualCardDetails.expiry_date;
          delete virtualCardDetails.card_security_code;
        }
        navigate('/recovered', { state: { virtualCardDetails } });
      } catch (e) {
        console.log(e);
        setLoading(false);
        handleApiError(e);
      }
    };

    const handleOnRecoverClicked = async e => {
      e.preventDefault();
      // Throws a generic error message if any of the fields are not valid
      if (checkFormValidation()) return;

      // show warning modal if form is valid
      setShowModal(true);
    };

    const toggleModal = () => {
      setShowModal(!showModal);
    };

    const disabled = false;

    const renderForm = () => (
      <Form
        id="recover"
        onSubmit={disabled ? () => {} : handleOnRecoverClicked}
      >
        <FormGroup form="recover">
          <InputContainer>
            <InputGroup
              dataId="firstName"
              label={recoverEn['recover-firstName-label']}
              labelFor="firstName"
            >
              <Input
                id="firstName"
                className={`${validationErrors.firstName ? 'is-invalid' : ''}`}
                value={inputInfo.firstName}
                type="text"
                autoComplete="off"
                onBlur={e => {
                  if (!e.target.value.trim()) {
                    setValidationErrors({
                      key: 'firstName',
                      value: 'First Name is required',
                    });
                  } else {
                    setValidationErrors({
                      key: 'firstName',
                      value: '',
                    });
                  }
                }}
                onChange={e => {
                  handleOnChange('firstName', e.target.value);
                }}
                placeholder="John"
              />
              {validationErrors.firstName && (
                <FieldError className="invalid-feedback">
                  {validationErrors.firstName}
                </FieldError>
              )}
            </InputGroup>
            <InputGroup
              dataId="lastName"
              label={recoverEn['recover-lastName-label']}
              labelFor="lastName"
            >
              <Input
                id="lastName"
                className={`${validationErrors.lastName ? 'is-invalid' : ''}`}
                value={inputInfo.lastName}
                type="text"
                autoComplete="off"
                onBlur={e => {
                  if (!e.target.value.trim()) {
                    setValidationErrors({
                      key: 'lastName',
                      value: 'Last Name is required',
                    });
                  } else {
                    setValidationErrors({
                      key: 'lastName',
                      value: '',
                    });
                  }
                }}
                onChange={e => {
                  handleOnChange('lastName', e.target.value);
                }}
                placeholder="Doe"
              />
              {validationErrors.lastName && (
                <FieldError className="invalid-feedback">
                  {validationErrors.lastName}
                </FieldError>
              )}
            </InputGroup>
          </InputContainer>
          <InputContainer>
            <InputGroup
              dataId="email"
              label={recoverEn['recover-email-label']}
              labelFor="email"
            >
              <Input
                id="email"
                className={`${validationErrors.email ? 'is-invalid' : ''}`}
                value={inputInfo.email}
                type="email"
                autoComplete="off"
                onBlur={e => {
                  if (!e.target.value.trim()) {
                    setValidationErrors({
                      key: 'email',
                      value: 'Email is required',
                    });
                  } else if (
                    e.target.value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
                  ) {
                    setValidationErrors({
                      key: 'email',
                      value: '',
                    });
                  } else {
                    setValidationErrors({
                      key: 'email',
                      value:
                        'Invalid Email Address. Please check and try again.',
                    });
                  }
                }}
                onChange={e => {
                  handleOnChange('email', e.target.value);
                }}
                placeholder="johndoe@email.com"
              />
              {validationErrors.email && (
                <FieldError className="invalid-feedback">
                  {validationErrors.email}
                </FieldError>
              )}
            </InputGroup>
            <InputGroup
              dataId="accessCode"
              label={recoverEn['recover-accessCode-label']}
              labelFor="accessCode"
            >
              <Input
                id="accessCode"
                className={`${validationErrors.accessCode ? 'is-invalid' : ''}`}
                value={inputInfo.accessCode}
                type="text"
                autoComplete="off"
                onBlur={e => {
                  if (!e.target.value.trim()) {
                    setValidationErrors({
                      key: 'accessCode',
                      value: 'Access Code is required',
                    });
                  } else {
                    setValidationErrors({
                      key: 'accessCode',
                      value: '',
                    });
                  }
                }}
                onChange={e => {
                  handleOnChange('accessCode', e.target.value);
                }}
                placeholder="Enter Access code"
              />
              {validationErrors.accessCode && (
                <FieldError className="invalid-feedback">
                  {validationErrors.accessCode}
                </FieldError>
              )}
            </InputGroup>
          </InputContainer>
          <br/>
        </FormGroup>
        {agreementData && agreementData.cardholder_fee_disclosure_url && (
          <div style={{ margin: '16px 0' }}>
            <img
              src={agreementData.cardholder_fee_disclosure_url}
              alt="Fee Disclosure"
              width="100%"
            />
          </div>
        )}
        <SubmitButton
          buttonType="primary"
          size="large"
          value={intl.formatMessage({
            id: 'recover-submit-button',
            defaultMessage: recoverEn['recover-submit-button'],
          })}
          disabled={disabled}
        />
      </Form>
    );

    const renderBody = () => {
      if (!showModal) return renderForm();
      if (showModal)
        return (
          <>
            {renderForm()}
            <StyledModal key="recover-card-modal">
              {loading ? (
                <Loader />
              ) : (
                <>
                  <Title>{recoverEn['recover-modal-title']}</Title>
                  <StyledAlert type="warn">
                    {intl.formatMessage({
                      id: 'recover-modal-text',
                      defaultMessage: recoverEn['recover-modal-text'],
                    })}
                    <br />
                    <br />
                    {intl.formatMessage({
                      id: 'recover-modal-proceed-text',
                      defaultMessage:
                        recoverEn['recover-modal-proceed-text'],
                    })}
                  </StyledAlert>
                  <ActionBar>
                    <ButtonGroup reverse justifyContent="center">
                      <StyledButton
                        type="primary"
                        size="large"
                        onClick={handleOnSubmit}
                      >
                        {intl.formatMessage({
                          id: 'recover-modal-proceed-button',
                          defaultMessage:
                            recoverEn['recover-modal-proceed-button'],
                        })}
                      </StyledButton>
                      <StyledButton onClick={toggleModal} size="large">
                        {intl.formatMessage({
                          id: 'recover-modal-cancel-button',
                          defaultMessage:
                            recoverEn['recover-modal-cancel-button'],
                        })}
                      </StyledButton>
                    </ButtonGroup>
                  </ActionBar>
                </>
              )}
            </StyledModal>
          </>
        );
      return null;
    };

    return <div>{renderBody()}</div>;
  }
);
